import React from "react";
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError, formatRupiah } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Plus } from "lucide-react";

const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia","Austria",
  "Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan",
  "Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cabo Verde","Cambodia",
  "Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia","Comoros","Congo","Costa Rica",
  "Croatia","Cuba","Cyprus","Czech Republic","Denmark","Djibouti","Dominica","Dominican Republic","Ecuador","Egypt",
  "El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France","Gabon",
  "Gambia","Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau","Guyana",
  "Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel",
  "Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati","Kuwait","Kyrgyzstan","Laos",
  "Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania","Luxembourg","Madagascar","Malawi",
  "Malaysia","Maldives","Mali","Malta","Marshall Islands","Mauritania","Mauritius","Mexico","Micronesia","Moldova",
  "Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia","Nauru","Nepal","Netherlands",
  "New Zealand","Nicaragua","Niger","Nigeria","North Korea","North Macedonia","Norway","Oman","Pakistan","Palau",
  "Palestine","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania",
  "Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines","Samoa","San Marino",
  "Sao Tome and Principe","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone","Singapore","Slovakia",
  "Slovenia","Solomon Islands","Somalia","South Africa","South Korea","South Sudan","Spain","Sri Lanka","Sudan",
  "Suriname","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania","Thailand","Timor-Leste","Togo",
  "Tonga","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda","Ukraine","United Arab Emirates",
  "United Kingdom","United States","Uruguay","Uzbekistan","Vanuatu","Vatican City","Venezuela","Vietnam",
  "Yemen","Zambia","Zimbabwe"
];

export default function Checkout() {
  const { items, total, clear } = useCart();
  const { user, refresh } = useAuth();
  const nav = useNavigate();

  const [method, setMethod] = useState("balance");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddr, setSelectedAddr] = useState(null);
  const [useManual, setUseManual] = useState(false);
  const [addr, setAddr] = useState({
    recipient_name: "", phone: "", address: "",
    district: "", city: "", province: "", postal_code: "", country: "Indonesia",
  });

  const hasPhysical = items.some((i) => i.type === "physical");

  useEffect(() => {
    if (hasPhysical) {
      api.get("/me/addresses").then((r) => {
        setSavedAddresses(r.data);
        if (r.data.length > 0) {
          setSelectedAddr(r.data[0]);
          setUseManual(false);
        } else {
          setUseManual(true);
        }
      }).catch(() => setUseManual(true));
    }
  }, [hasPhysical]);

  const getFullAddress = () => {
    if (selectedAddr && !useManual) {
      return `${selectedAddr.recipient_name} | ${selectedAddr.phone} | ${selectedAddr.address}, ${selectedAddr.district}, ${selectedAddr.city}, ${selectedAddr.province} ${selectedAddr.postal_code}, ${selectedAddr.country}`;
    }
    return `${addr.recipient_name} | ${addr.phone} | ${addr.address}, ${addr.district}, ${addr.city}, ${addr.province} ${addr.postal_code}, ${addr.country}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        payment_method: method,
        shipping_address: hasPhysical ? getFullAddress() : null,
        notes,
      });
      clear();
      await refresh();
      toast.success("Pesanan dibuat!");
      nav(`/orders/${data.id}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return <div className="p-12 font-mono text-sm">// Keranjang kosong</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-10" data-testid="checkout-page">
      <div className="label-mono text-zinc-500">// CHECKOUT</div>
      <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-1">Checkout</h1>

      <form onSubmit={submit} className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">

          <section className="border border-black bg-white p-6">
            <div className="label-mono text-zinc-500">// PAYMENT METHOD</div>
            <h2 className="font-display text-xl font-black uppercase mt-1 mb-4">Metode Pembayaran</h2>
            <div className="space-y-3">
              <label className={`flex items-start gap-3 p-4 border cursor-pointer ${method === "balance" ? "border-black bg-[#FFD600]" : "border-zinc-300"}`}>
                <input type="radio" checked={method === "balance"} onChange={() => setMethod("balance")} />
                <div className="flex-1">
                  <div className="font-mono text-sm font-bold uppercase">Saldo Deposit</div>
                  <div className="text-xs text-zinc-600 mt-1">Saldo: <b>{formatRupiah(user?.balance || 0)}</b> · Order langsung diproses</div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-4 border cursor-pointer ${method === "manual_transfer" ? "border-black bg-[#FFD600]" : "border-zinc-300"}`}>
                <input type="radio" checked={method === "manual_transfer"} onChange={() => setMethod("manual_transfer")} />
                <div className="flex-1">
                  <div className="font-mono text-sm font-bold uppercase">Transfer Manual</div>
                  <div className="text-xs text-zinc-600 mt-1">Bayar via transfer bank, konfirmasi manual oleh admin</div>
                </div>
              </label>
            </div>
          </section>

          {hasPhysical && (
            <section className="border border-black bg-white p-6">
              <div className="label-mono text-zinc-500">// SHIPPING ADDRESS</div>
              <h2 className="font-display text-xl font-black uppercase mt-1 mb-4">Alamat Pengiriman</h2>

              {/* Alamat Tersimpan */}
              {savedAddresses.length > 0 && (
                <div className="mb-4">
                  <div className="label-mono text-zinc-500 mb-2">Pilih Alamat Tersimpan:</div>
                  <div className="space-y-2">
                    {savedAddresses.map((a) => (
                      <label key={a.id} className={`flex items-start gap-3 p-3 border cursor-pointer ${selectedAddr?.id === a.id && !useManual ? "border-black bg-zinc-50" : "border-zinc-200"}`}>
                        <input type="radio" checked={selectedAddr?.id === a.id && !useManual}
                          onChange={() => { setSelectedAddr(a); setUseManual(false); }} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3 h-3 text-[#FF6A00]" />
                            <span className="font-mono text-xs font-black uppercase bg-[#FFD600] px-1.5 py-0.5 border border-black">{a.label}</span>
                          </div>
                          <div className="font-mono text-xs font-bold mt-1">{a.recipient_name} · {a.phone}</div>
                          <div className="font-mono text-[10px] text-zinc-500">{a.address}, {a.district}, {a.city}, {a.province} {a.postal_code}</div>
                        </div>
                      </label>
                    ))}
                    <label className={`flex items-start gap-3 p-3 border cursor-pointer ${useManual ? "border-black bg-zinc-50" : "border-zinc-200"}`}>
                      <input type="radio" checked={useManual} onChange={() => setUseManual(true)} />
                      <div className="flex items-center gap-2">
                        <Plus className="w-3 h-3" />
                        <span className="font-mono text-xs font-bold">Isi alamat baru</span>
                      </div>
                    </label>
                  </div>
                  <Link to="/addresses" className="font-mono text-[10px] text-[#FF6A00] underline mt-2 inline-block">
                    + Kelola alamat tersimpan →
                  </Link>
                </div>
              )}

              {/* Form Manual */}
              {(useManual || savedAddresses.length === 0) && (
                <div className="grid md:grid-cols-2 gap-4">
                  {savedAddresses.length === 0 && (
                    <div className="md:col-span-2 p-3 border border-dashed border-zinc-300 bg-zinc-50">
                      <div className="font-mono text-xs text-zinc-500">
                        Belum ada alamat tersimpan. <Link to="/addresses" className="text-[#FF6A00] underline font-bold">Simpan alamat</Link> untuk checkout lebih cepat.
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="label-mono block mb-2">Nama Penerima</label>
                    <input value={addr.recipient_name} onChange={(e) => setAddr({ ...addr, recipient_name: e.target.value })}
                      className="input-brutal" placeholder="Nama lengkap penerima" required />
                  </div>
                  <div>
                    <label className="label-mono block mb-2">No. Handphone</label>
                    <input value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })}
                      className="input-brutal" placeholder="08xxxxxxxxxx" required />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label-mono block mb-2">Alamat Lengkap</label>
                    <textarea value={addr.address} onChange={(e) => setAddr({ ...addr, address: e.target.value })}
                      rows={3} className="input-brutal" placeholder="Nama jalan, nomor rumah, RT/RW, kelurahan" required />
                  </div>
                  <div>
                    <label className="label-mono block mb-2">Kecamatan</label>
                    <input value={addr.district} onChange={(e) => setAddr({ ...addr, district: e.target.value })}
                      className="input-brutal" placeholder="Kecamatan" required />
                  </div>
                  <div>
                    <label className="label-mono block mb-2">Kota / Kabupaten</label>
                    <input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })}
                      className="input-brutal" placeholder="Kota atau Kabupaten" required />
                  </div>
                  <div>
                    <label className="label-mono block mb-2">Provinsi</label>
                    <input value={addr.province} onChange={(e) => setAddr({ ...addr, province: e.target.value })}
                      className="input-brutal" placeholder="Provinsi" required />
                  </div>
                  <div>
                    <label className="label-mono block mb-2">Kode Pos</label>
                    <input value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })}
                      className="input-brutal" placeholder="12345" required />
                  </div>
                  <div>
                    <label className="label-mono block mb-2">Negara</label>
                    <select value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })}
                      className="input-brutal" required>
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </section>
          )}

          <section className="border border-black bg-white p-6">
            <div className="label-mono text-zinc-500">// NOTES</div>
            <h2 className="font-display text-xl font-black uppercase mt-1 mb-4">Catatan (opsional)</h2>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
              className="input-brutal" placeholder="ID game, server, atau catatan lain" />
          </section>
        </div>

        <aside className="border border-black bg-white p-6 shadow-brutal h-fit lg:sticky lg:top-20">
          <div className="label-mono text-zinc-500">// REVIEW</div>
          <h2 className="font-display text-2xl font-black uppercase mt-1">Total</h2>
          <div className="space-y-2 mt-4 font-mono text-sm">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between text-zinc-700">
                <span className="truncate pr-2">{i.name} ×{i.quantity}</span>
                <span>{formatRupiah(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-black mt-4 pt-4 flex justify-between items-end">
            <span className="label-mono">Total Bayar</span>
            <span className="font-mono text-2xl font-black">{formatRupiah(total)}</span>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full mt-6">
            {loading ? "// PROCESSING..." : "Buat Pesanan →"}
          </button>
        </aside>
      </form>
    </div>
  );
}